// ==============================
// Imports
// ==============================

// Libraries
import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

// Local components
import { RequireAuth } from './components/RequireAuth';
import TrialBooking from './pages/TrialBooking';
import Service from './pages/Registration';
import Assessment from './pages/Assessment';
import Class from './pages/Class';
import Report from './pages/Report/Report';
import Client from './pages/Client';
import Home from './pages/Home';
import Signin from './pages/Signin';
// import Signup from './pages/Signup';
import ServiceSetting from './pages/Setting/ServiceSetting';
import CourseSetting from './pages/Setting/CourseSetting';
import ImportDataSetting from './pages/Setting/ImportDataSetting';
import AuditLogSetting from './pages/Setting/AuditLogSetting';
import AccountManagement from './pages/Setting/AccountManagement';
import ParentLookupSetting from './pages/Setting/ParentLookupSetting';
import AssessmentHome from './pages/AssessmentPages/AssessmentHome';
import Coach from './pages/Coach';
import CoachClass from './pages/CoachClass';
import CoachMasterData from './pages/CoachMasterData';
import CustomerIntakeForm from './pages/CustomerIntakeForm';
import CustomerIntakePrivacy from './pages/CustomerIntakePrivacy';
import CustomerIntakeQr from './pages/CustomerIntakeQr';
import CustomerIntakeResponses from './pages/CustomerIntakeResponses';
import CustomerIntakeTemplates from './pages/CustomerIntakeTemplates';
import CustomerSurveyResponses from './pages/CustomerSurveyResponses';
import ParentLookup from './pages/ParentLookup';
import ParentLookupAdmin from './pages/ParentLookupAdmin';
import PendingEnrollment from './pages/PendingEnrollment';
import UserGuide from './pages/UserGuide';

// ==============================
// Render
// ==============================
const App: React.FC = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ToastContainer />
      <Routes>
        {/* Sign in route */}
        <Route path="signin" element={<Signin />} />
        {/* Sign up route */}
        {/* <Route path="signup" element={<Signup />} /> */}

        {/* Public customer survey routes */}
        <Route path="customer-survey/qr" element={<CustomerIntakeQr />} />
        <Route path="customer-survey/form" element={<CustomerIntakeForm />} />
        <Route path="parent-lookup" element={<ParentLookup />} />
        <Route
          path="parent-lookup-admin"
          element={
            <RequireAuth roles={['ADMIN']}>
              <ParentLookupAdmin />
            </RequireAuth>
          }
        />
        <Route
          path="customer-survey/privacy"
          element={<CustomerIntakePrivacy />}
        />
        <Route
          path="customer-survey/templates"
          element={
            <RequireAuth roles={['ADMIN']}>
              <CustomerIntakeTemplates />
            </RequireAuth>
          }
        />
        <Route
          path="customer-survey/responses"
          element={
            <RequireAuth roles={['ADMIN']}>
              <CustomerSurveyResponses />
            </RequireAuth>
          }
        />
        <Route
          path="customer-survey/templates/:templateId/responses"
          element={
            <RequireAuth roles={['ADMIN']}>
              <CustomerIntakeResponses />
            </RequireAuth>
          }
        />
        {/* Main layout route */}
        <Route
          path="/"
          element={
            <RequireAuth roles={['ADMIN']}>
              <Home />
            </RequireAuth>
          }
        />
        {/* Home route */}
        <Route
          path="home"
          element={
            <RequireAuth roles={['ADMIN']}>
              <Home />
            </RequireAuth>
          }
        />
        {/* Client route */}
        <Route
          path="client"
          element={
            <RequireAuth roles={['ADMIN']}>
              <Client />
            </RequireAuth>
          }
        />
        {/* Coach route */}
        <Route
          path="coach"
          element={
            <RequireAuth roles={['ADMIN']}>
              <Coach />
            </RequireAuth>
          }
        />
        {/* Trial Booking route */}
        <Route
          path="trial-booking"
          element={
            <RequireAuth roles={['ADMIN']}>
              <TrialBooking />
            </RequireAuth>
          }
        />
        {/* Service route */}
        <Route
          path="registration"
          element={
            <RequireAuth roles={['ADMIN']}>
              <Service />
            </RequireAuth>
          }
        />
        {/* Assessment route */}
        <Route
          path="assessment"
          element={
            <RequireAuth roles={['ADMIN']}>
              <Assessment />
            </RequireAuth>
          }
        />
        {/* Class route */}
        <Route
          path="class"
          element={
            <RequireAuth roles={['ADMIN']}>
              <Class />
            </RequireAuth>
          }
        />
        {/* Report route */}
        <Route
          path="report"
          element={
            <RequireAuth roles={['ADMIN']}>
              <Report />
            </RequireAuth>
          }
        />
        {/* Service Setting route */}
        <Route
          path="service-setting"
          element={
            <RequireAuth roles={['ADMIN']}>
              <ServiceSetting />
            </RequireAuth>
          }
        />
        {/* Course Setting route */}
        <Route
          path="course-setting"
          element={
            <RequireAuth roles={['ADMIN']}>
              <CourseSetting />
            </RequireAuth>
          }
        />
        {/* Import Data (Excel import/export) route */}
        <Route
          path="import-data"
          element={
            <RequireAuth roles={['ADMIN']}>
              <ImportDataSetting />
            </RequireAuth>
          }
        />
        {/* Audit log route */}
        <Route
          path="audit-log"
          element={
            <RequireAuth roles={['ADMIN']}>
              <AuditLogSetting />
            </RequireAuth>
          }
        />

        {/* Account management route */}
        <Route
          path="account-management"
          element={
            <RequireAuth roles={['ADMIN']}>
              <AccountManagement />
            </RequireAuth>
          }
        />
        {/* Parent lookup setting route */}
        <Route
          path="parent-lookup-setting"
          element={
            <RequireAuth roles={['ADMIN']}>
              <ParentLookupSetting />
            </RequireAuth>
          }
        />

        {/* Assessment Home route */}
        <Route
          path="assessment-home"
          element={
            <RequireAuth roles={['COACH']}>
              <AssessmentHome />
            </RequireAuth>
          }
        />
        {/* Coach Class route */}
        <Route
          path="coach-class"
          element={
            <RequireAuth roles={['COACH']}>
              <CoachClass />
            </RequireAuth>
          }
        />
        {/* Coach Master Data route */}
        <Route
          path="coach-master-data"
          element={
            <RequireAuth roles={['COACH']}>
              <CoachMasterData />
            </RequireAuth>
          }
        />
        {/* Admin Master Data route */}
        <Route
          path="master-data"
          element={
            <RequireAuth roles={['ADMIN']}>
              <CoachMasterData isAdmin />
            </RequireAuth>
          }
        />
        <Route
          path="pending-enrollment"
          element={
            <RequireAuth roles={['ADMIN', 'COACH']}>
              <PendingEnrollment />
            </RequireAuth>
          }
        />
        {/* User guide route */}
        <Route
          path="user-guide"
          element={
            <RequireAuth roles={['ADMIN', 'COACH']}>
              <UserGuide />
            </RequireAuth>
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
